/* Home V2: capability systems — pillar open/close.

   A pillar tab opens its box in place (flex-grow rail states + one measured
   height animation); the box shows the pillar's panel description and its
   offerings list. Each offering is a plain link to that pillar's dedicated
   offerings page (section-systems.php), so there is no in-page drill-down: the
   old list→detail view swap, sibling chips and breadcrumb have been removed.
   Only opening, closing (the × button / Escape) and keeping the open box's
   measured height correct on resize remain here.

   No dependency on window.PEHomeV2's scroll engine: this component is
   click-driven. window.PEHomeV2.utils.jump / .reduce are reused when present
   (00-engine.js), with local fallbacks so this file still works standalone. */
(function () {
  'use strict';

  var pillarsEl = document.getElementById('pillars');
  if (!pillarsEl) { return; }

  var reduce = (window.PEHomeV2 && window.PEHomeV2.reduce) ||
    (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);

  var jump = (window.PEHomeV2 && window.PEHomeV2.utils && window.PEHomeV2.utils.jump) || function (dy) {
    var h = document.documentElement, prev = h.style.scrollBehavior;
    h.style.scrollBehavior = 'auto';
    window.scrollBy(0, dy);
    h.style.scrollBehavior = prev;
  };

  function $(sel, r) { return (r || document).querySelector(sel); }
  function $$(sel, r) { return Array.prototype.slice.call((r || document).querySelectorAll(sel)); }

  var pillars = $$('.pillar', pillarsEl);
  var openKey = null;

  /* Shared stage floor (desktop): every pillar opens to the height of the
     tallest pillar's offerings view, so a 3-offering card (TenX) lands the
     same size as the 4/5-offering ones — the list rows flex apart to fill
     (see _pillars.scss .p-view[data-view="list"]). Set per open/resize by
     measureFinal/sizeBody, cleared on close. */
  var stageFloor = 0;
  function equaliseHeights() {
    return window.matchMedia && window.matchMedia('(min-width: 761px)').matches;
  }

  function sizeBody(pillar) {
    if (!pillar || !pillar.classList.contains('is-open')) { return; }
    var body = $('.p-body', pillar);
    var inner = $('.p-inner', pillar);
    var stage = $('.p-stage', pillar);
    var act = $('.p-view[data-state="active"]', pillar);
    if (!act) { return; }
    stage.style.height = Math.max(act.offsetHeight, stageFloor) + 'px';
    body.style.height = inner.offsetHeight + 'px';
  }

  /* Animation guard. The final geometry is measured ONCE before anything
     animates, the transitions run toward constant pixel targets, and one
     settle pass trues everything up after they finish. While the transitions
     run, nothing may re-measure (a mid-flight write restarts the transition
     toward a moving target, which reads as judder). */
  var animating = false, settleTimer = null;
  function settle(pillar) {
    animating = true;
    clearTimeout(settleTimer);
    settleTimer = setTimeout(function () {
      animating = false;
      sizeBody(pillar || pillarsEl.querySelector('.pillar.is-open'));
    }, 740);
  }

  /* After an open, make sure the whole card ends inside the viewport: scroll
     just enough that the card bottom clears the fold, without pushing the card
     top under the fixed site header. Runs concurrently with the 0.68s open
     animation (both were measured up front, so the scroll target is final).
     Native scrollBy({behavior:'smooth'}) can't be used here: the card's height
     transition mutates layout every frame, which makes Chrome cancel the
     smooth scroll — so the glide is driven by rAF on the instant jump()
     primitive instead. User wheel/touch input aborts it. */
  var NAV_CLEAR = 96, FOLD_PAD = 24, scrollRaf = null;
  function animateScrollBy(dy) {
    if (scrollRaf) { cancelAnimationFrame(scrollRaf); scrollRaf = null; }
    if (reduce) { jump(dy); return; }
    var startY = window.scrollY || window.pageYOffset;
    var t0 = null, DUR = 680;
    /* One absolute, rounded scrollTo per frame (a per-frame scrollBy delta
       accumulates fractional rounding into visible stutter), with smooth
       scroll-behavior and scroll anchoring both suspended for the glide —
       anchoring otherwise counter-adjusts against the growing card between
       frames, which reads as jitter. */
    var html = document.documentElement;
    var prevBehavior = html.style.scrollBehavior, prevAnchor = html.style.overflowAnchor;
    html.style.scrollBehavior = 'auto';
    html.style.overflowAnchor = 'none';
    function cleanup() {
      html.style.scrollBehavior = prevBehavior;
      html.style.overflowAnchor = prevAnchor;
      window.removeEventListener('wheel', cancel);
      window.removeEventListener('touchstart', cancel);
    }
    function cancel() {
      if (scrollRaf) { cancelAnimationFrame(scrollRaf); scrollRaf = null; }
      cleanup();
    }
    window.addEventListener('wheel', cancel, { passive: true });
    window.addEventListener('touchstart', cancel, { passive: true });
    function step(ts) {
      if (t0 === null) { t0 = ts; }
      var t = Math.min(1, (ts - t0) / DUR);
      var e = 1 - Math.pow(1 - t, 3); /* ease-out, close to the card's curve */
      window.scrollTo(0, Math.round(startY + dy * e));
      if (t < 1) { scrollRaf = requestAnimationFrame(step); }
      else { scrollRaf = null; cleanup(); }
    }
    scrollRaf = requestAnimationFrame(step);
  }
  function scrollToFit(openEl, cardH) {
    var top = openEl.getBoundingClientRect().top;
    var overflow = (top + cardH) - (window.innerHeight - FOLD_PAD);
    if (overflow <= 0) { return; }
    var dy = Math.min(overflow, Math.max(0, top - NAV_CLEAR));
    if (dy <= 0) { return; }
    animateScrollBy(dy);
  }

  function applyClasses(key) {
    if (key) { pillarsEl.dataset.open = key; } else { pillarsEl.removeAttribute('data-open'); }
    pillars.forEach(function (el) {
      var isOpen = el.dataset.key === key;
      el.classList.toggle('is-open', isOpen);
      el.classList.toggle('is-rail', !!key && !isOpen);
      el.classList.toggle('is-collapsed', !key);
    });
  }

  /* Measure the opening panel's FINAL height before anything animates: apply
     the target classes with transitions suppressed, read the settled geometry
     at the final width, then revert. All of it happens before paint, so
     nothing flashes on screen.

     Desktop: the stage target is the MAX of all three pillars' offerings
     views (each measured in its own open state — the open width is identical
     for all of them), so every card opens to the same height and the shorter
     lists space out to fill. Narrow screens keep per-pillar heights. */
  function measureFinal(nextKey, prevKey) {
    var openEl = pillarsEl.querySelector('.pillar[data-key="' + nextKey + '"]');
    if (!openEl) { return null; }
    var inline = pillars.map(function (el) { return $('.p-body', el).style.height; });
    pillarsEl.classList.add('is-measuring');

    /* Natural geometry of the pillar being opened. */
    function readOpen(p) {
      applyClasses(p.dataset.key);
      var v = $('.p-view[data-state="active"]', p);
      var st = $('.p-stage', p);
      var saved = st.style.height;
      var stageH = v ? v.offsetHeight : 0;
      st.style.height = stageH + 'px';
      var innerH = $('.p-inner', p).offsetHeight;
      st.style.height = saved;
      return { stage: stageH, inner: innerH };
    }

    var own = readOpen(openEl);
    var stageH = own.stage;
    var bodyH = own.inner;
    if (equaliseHeights()) {
      /* Every card opens to the height of the tallest card (its full inner:
         header bar + description + offerings view). A shorter pillar's
         shortfall — fewer offerings AND/OR a shorter description — is given
         to its stage, so the offer rows flex apart and the card fills the
         same box as the others. */
      var maxInner = own.inner;
      pillars.forEach(function (p) {
        if (p === openEl) { return; }
        var m = readOpen(p);
        if (m.inner > maxInner) { maxInner = m.inner; }
      });
      stageH = own.stage + (maxInner - own.inner);
      bodyH = maxInner;
    }

    applyClasses(prevKey);
    pillars.forEach(function (el, i) { $('.p-body', el).style.height = inline[i]; });
    void pillarsEl.offsetWidth; /* reflow at the start state before re-enabling transitions */
    pillarsEl.classList.remove('is-measuring');
    return { stage: stageH, body: bodyH };
  }

  if (window.ResizeObserver) {
    var ro = new ResizeObserver(function (entries) {
      if (animating) { return; } /* mid-animation writes would restart the height transition */
      entries.forEach(function (e) {
        var p = e.target.closest('.pillar');
        if (p && p.classList.contains('is-open') && e.target.dataset.state === 'active') { sizeBody(p); }
      });
    });
    $$('.p-view', pillarsEl).forEach(function (v) { ro.observe(v); });
  }

  /* noScroll: programmatic opens (orbit clicks / sessionStorage init) own
     their scrolling in 20-orbits.js — only user tab clicks glide-to-fit. */
  function setState(nextKey, noScroll) {
    var anchor = nextKey
      ? pillarsEl.querySelector('.pillar[data-key="' + nextKey + '"]')
      : (pillarsEl.querySelector('.pillar.is-open') || pillars[0]);
    if (!anchor) { return; }
    var before = anchor.getBoundingClientRect().top;

    var prevKey = openKey;
    openKey = nextKey;

    /* measureFinal also runs under reduced motion: transitions are killed by
       CSS there, so the pixel targets (incl. the shared stage floor) apply
       instantly instead of animating. */
    var target = nextKey ? measureFinal(nextKey, prevKey) : null;
    stageFloor = target ? target.stage : 0;

    applyClasses(nextKey);
    pillars.forEach(function (el) {
      var isOpen = el.dataset.key === nextKey;
      $('.p-tab', el).setAttribute('aria-expanded', isOpen ? 'true' : 'false');
      var body = $('.p-body', el);
      if (isOpen) {
        body.removeAttribute('inert');
      } else {
        body.setAttribute('inert', '');
        body.style.height = '0px';
      }
      /* fallback for browsers without inert support */
      $$('a, button', body).forEach(function (b) { b.tabIndex = isOpen ? 0 : -1; });
    });

    var openEl = null, finalH = 0;
    if (nextKey) {
      openEl = pillarsEl.querySelector('.pillar[data-key="' + nextKey + '"]');
      if (target) {
        /* constant targets: CSS interpolates width and height in one motion */
        $('.p-stage', openEl).style.height = target.stage + 'px';
        $('.p-body', openEl).style.height = target.body + 'px';
        finalH = target.body;
      } else {
        sizeBody(openEl); /* reduced-motion path: instant */
        finalH = openEl.offsetHeight;
      }
      settle(openEl);
    } else {
      settle(null);
    }

    /* hold the clicked pillar visually still: nothing shifts under the cursor */
    var after = anchor.getBoundingClientRect().top;
    if (Math.abs(after - before) > 1) { jump(after - before); }

    /* then bring the whole card into the field of view */
    if (openEl && !noScroll) { scrollToFit(openEl, finalH); }
  }

  pillarsEl.addEventListener('click', function (e) {
    var t;
    if ((t = e.target.closest('.p-tab'))) {
      var p = t.closest('.pillar');
      if (p.classList.contains('is-open')) { setState(null); t.focus({ preventScroll: true }); }
      else {
        /* Open in place — no scroll. The panel expands where it sits; the
           row's top is fixed, so nothing above moves. */
        setState(p.dataset.key);
        var land = $('.offer', p);
        if (land) { land.focus({ preventScroll: true }); }
      }
      return;
    }
    if ((t = e.target.closest('.p-close'))) {
      var pil = t.closest('.pillar'), tab = $('.p-tab', pil);
      setState(null);
      tab.focus({ preventScroll: true });
      return;
    }
    /* .offer links are plain navigations — left to the browser. */
  });

  /* escape closes the open pillar */
  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape' || !openKey) { return; }
    var p = pillarsEl.querySelector('.pillar.is-open');
    if (!p) { return; }
    var tab = $('.p-tab', p);
    setState(null);
    tab.focus({ preventScroll: true });
  });

  /* keep an open pillar's measured height correct across viewport resizes
     (re-derive the shared stage floor first — it is width-dependent) */
  var resizeTimer;
  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var open = pillarsEl.querySelector('.pillar.is-open');
      if (!open) { return; }
      var target = measureFinal(openKey, openKey);
      stageFloor = target ? target.stage : 0;
      sizeBody(open);
    }, 140);
  });

  /* Public hook for same-page callers (20-orbits.js orbit clicks): performs
     the same open as a tab click. Scrolling is left to the caller (the orbit's
     own scrollIntoView to #pe-systems). */
  window.PEHomeV2 = window.PEHomeV2 || {};
  window.PEHomeV2.openPillar = function (i) {
    var key = String(i);
    if (!pillarsEl.querySelector('.pillar[data-key="' + key + '"]')) { return; }
    setState(key, true);
  };

  /* Orbit clicks (20-orbits.js) also record which pillar was chosen in
     sessionStorage['hv2OpenPillar'] ('0'/'1'/'2') as a fallback before jumping
     to #pe-systems. Consume it once on init and open that pillar. */
  (function initFromSessionStorage() {
    var storedIdx = null;
    try {
      storedIdx = sessionStorage.getItem('hv2OpenPillar');
      if (storedIdx !== null) { sessionStorage.removeItem('hv2OpenPillar'); }
    } catch (err) { storedIdx = null; }
    if (storedIdx === null) { return; }
    if (!pillarsEl.querySelector('.pillar[data-key="' + storedIdx + '"]')) { return; }
    setState(storedIdx, true);
  })();
})();
